import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import UserDashboardShell from '../../components/user/UserDashboardShell';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  createDefaultPaymentMethods,
  readUserDashboardState,
  writeUserDashboardState,
} from '../../utils/userDashboardState';
import {
  INITIAL_CARD_FORM,
  formatCardNumber,
  formatExpiry,
  getCardBrand,
  getCardDigits,
} from '../../utils/paymentCard';

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 7h14" />
    <path d="M9 7V4.5h6V7" />
    <path d="M8 7v11a1.5 1.5 0 0 0 1.5 1.5h5A1.5 1.5 0 0 0 16 18V7" />
    <path d="M10 10.5v5" />
    <path d="M14 10.5v5" />
  </svg>
);

const maskDigits = (last4) => `.... .... .... ${last4}`;

const PaymentMethodForm = ({ formData, onChange, onSubmit }) => {
  const { t } = useLanguage();
  const brand = getCardBrand(formData.cardNumber);

  return (
    <form className="account-entry-form account-entry-form--payment" onSubmit={onSubmit}>
      <h2 className="account-entry-form__title">{t('payments.addTitle')}</h2>

      <label className="account-entry-form__field account-entry-form__field--card">
        <span className="sr-only">{t('payments.cardNumber')}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="cc-number"
          value={formData.cardNumber}
          placeholder="0000 0000 0000 0000"
          required
          onChange={(event) => onChange({ ...formData, cardNumber: formatCardNumber(event.target.value) })}
        />
        <span className="account-entry-form__card-brand">{brand}</span>
      </label>

      <label className="account-entry-form__field">
        <span className="sr-only">{t('payments.expirationDate')}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="cc-exp"
          value={formData.expiry}
          placeholder="01/27"
          required
          onChange={(event) => onChange({ ...formData, expiry: formatExpiry(event.target.value) })}
        />
      </label>

      <label className="account-entry-form__field">
        <span className="sr-only">{t('payments.securityCode')}</span>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="cc-csc"
          value={formData.cvv}
          placeholder="***"
          maxLength="4"
          required
          onChange={(event) => onChange({ ...formData, cvv: event.target.value.replace(/\D/g, '').slice(0, 4) })}
        />
      </label>

      <div className="account-entry-form__actions">
        <button type="submit" className="account-button account-button--outline account-entry-form__save">
          {t('payments.save')}
        </button>
        <button type="submit" className="account-button account-button--primary account-entry-form__next">
          {t('payments.pay')}
        </button>
      </div>
    </form>
  );
};

const PaymentMethods = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState(INITIAL_CARD_FORM);

  useEffect(() => {
    if (searchParams.get('add') === 'payment') {
      setIsAddingPayment(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user?.id) {
      setPaymentMethods([]);
      setHasLoaded(false);
      return;
    }

    const storedMethods = readUserDashboardState('payment-methods', user.id, createDefaultPaymentMethods());
    setPaymentMethods(storedMethods);
    setHasLoaded(true);
  }, [user?.id]);

  useEffect(() => {
    if (!hasLoaded || !user?.id) {
      return;
    }

    writeUserDashboardState('payment-methods', user.id, paymentMethods);
  }, [hasLoaded, paymentMethods, user?.id]);

  const handleDelete = (methodId) => {
    setPaymentMethods((currentMethods) => currentMethods.filter((method) => method.id !== methodId));
  };

  const handleAddPaymentMethod = (event) => {
    event.preventDefault();

    const cardDigits = getCardDigits(paymentForm.cardNumber);
    if (cardDigits.length < 4 || !paymentForm.expiry || !paymentForm.cvv) {
      return;
    }

    const brand = getCardBrand(paymentForm.cardNumber);

    setPaymentMethods((currentMethods) => [
      ...currentMethods,
      {
        id: `payment-method-${Date.now()}`,
        brand,
        bank: brand === 'MC' ? 'MASTER CARD' : 'VISA CARD',
        last4: cardDigits.slice(-4),
        expiry: paymentForm.expiry,
      },
    ]);
    setPaymentForm(INITIAL_CARD_FORM);
    setIsAddingPayment(false);
  };

  return (
    <UserDashboardShell>
      <section className="account-page">
        <h2 className="account-page__title">{t('payments.title')}</h2>

        <div className="account-payments__list">
          {paymentMethods.map((method) => (
            <div key={method.id} className="account-payments__item">
              <div className="account-payments__card">
                <span className="account-payments__brand">{method.brand}</span>
                <span className="account-payments__bank">{method.bank}</span>
                <span className="account-payments__digits">{maskDigits(method.last4)}</span>
              </div>

              <button type="button" className="account-delete" onClick={() => handleDelete(method.id)} aria-label={`Delete ${method.bank} card`}>
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>

        {isAddingPayment ? (
          <PaymentMethodForm
            formData={paymentForm}
            onChange={setPaymentForm}
            onSubmit={handleAddPaymentMethod}
          />
        ) : (
          <button type="button" className="account-button account-button--outline" onClick={() => setIsAddingPayment(true)}>
            {t('payments.addNew')}
          </button>
        )}
      </section>
    </UserDashboardShell>
  );
};

export default PaymentMethods;
